import React, { useEffect, useState } from "react";
import ProductCardSmall from "../pages/sellingArea/product/ProductCardSmall";
import { makeStyles, Theme, Typography } from "@material-ui/core";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { CartState } from "../pages/sellingArea/state/reducers/cart";
import moment from "moment";
import DataProvider from "../provider";
import { Skeleton } from "@material-ui/lab";

interface UpsellingCart {
	upselling: {
		id: string | undefined;
		title: string | undefined;
		subtitle: string | undefined;
	};
	items: any[];
}

const style = makeStyles((theme: Theme) => ({
	container: {
		padding: theme.spacing(0.5),
	},
	scrollable: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "flex-start",
		alignItems: "center",
		gap: "8px",
		overflowX: "scroll",
		padding: theme.spacing(0.2),
		marginTop: theme.spacing(1),
	},
	card: {
		flex: 1,
		width: "150px",
	},
	title: {
		whiteSpace: "nowrap",
		fontSize: "1.1rem",
		textOverflow: "ellipsis",
		overflow: "hidden",
	},
}));

export default function UpsellingRow(props) {
	const classes = style();
	const [status, setStatus] = useState("INITIAL");
	const cart: CartState = useSelector((state) => state.cart);
	const [itemSaleableInCartIds, setItemSaleableInCartIds] = useState<
		number[]
	>(cart.items.map((item) => item.itemSaleableId) || []);
	const [upsellingCart, setUpsellingCart] = useState<UpsellingCart>({
		upselling: {
			id: undefined,
			title: undefined,
			subtitle: undefined,
		},
		items: [],
	});
	const params = useParams();
	const dataProvider = DataProvider(
		process.env.REACT_APP_WAREHOUSE_API || ""
	);
	const sellingPointImpulseId = params["id"];

	useEffect(() => {
		const itemsInCartIds = cart.items.map((item) => item.itemSaleableId);
		if (itemsInCartIds.length !== itemSaleableInCartIds.length) {
			setItemSaleableInCartIds(itemsInCartIds);
			setStatus("INITIAL");
		}
	}, [cart.items, itemSaleableInCartIds.length]);

	useEffect(() => {
		if (cart.items.length > 0) {
			const itemsInCartIds = cart.items.map(
				(item) => item.itemSaleableId
			);
			setUpsellingCart((current) => {
				return {
					upselling: {
						id: current.upselling.id,
						title: current.upselling.title,
						subtitle: current.upselling.subtitle,
					},
					items: current.items.filter(
						(item) => !itemsInCartIds.includes(item.id)
					),
				};
			});
		}
	}, [cart]);

	useEffect(() => {
		if (status !== "INITIAL") {
			return;
		}
		setStatus("FETCHING");
		const localDateTime = moment().format("YYYY-MM-DDTHH:mm:ss");
		dataProvider(`b2c/upselling/${sellingPointImpulseId}/cart`, {
			method: "POST",
			body: JSON.stringify({
				itemSaleableIds: itemSaleableInCartIds,
				customerDateTime: localDateTime,
			}),
		})
			.then((response) => {
				if (!response.upselling.id) {
					return;
				}
				setUpsellingCart(response);
				setStatus("FETCHED");
			})
			.catch((error) => {
				console.log({
					error,
				});
				setStatus("ERROR_FETCHING");
			});
	}, [dataProvider, itemSaleableInCartIds, sellingPointImpulseId, status]);

	if (status === "ERROR_FETCHING" || upsellingCart.items.length === 0) {
		return null;
	}

	if (!upsellingCart.upselling.id) {
		return <LoadingSkeleton />;
	}

	return (
		<div
			className={`${classes.container} ${props.className}`}
			style={props.style}
		>
			<div>
				<Typography variant="h6" className={classes.title}>
					{upsellingCart.upselling.title || "Ti potrebbero piacere"}
				</Typography>
				{upsellingCart.upselling.subtitle && (
					<Typography variant="body2" color="textSecondary">
						{upsellingCart.upselling.subtitle}
					</Typography>
				)}
			</div>

			{status !== "FETCHED" && (
				<div
					style={{
						display: "flex",
						gap: "10px",
						padding: "10px 0",
						overflow: "hidden",
					}}
				>
					{[1, 2, 3].map((index) => {
						return (
							<Skeleton
								key={index}
								width={"150px"}
								height={180}
								style={{ transform: "none" }}
							></Skeleton>
						);
					})}
				</div>
			)}
			{status === "FETCHED" && (
				<div className={classes.scrollable}>
					{upsellingCart.items.map((item) => {
						return (
							<ProductCardSmall
								className={classes.card}
								showButton={true}
								showPrice={true}
								key={item.id}
								item={item}
								upsellingId={upsellingCart.upselling.id}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}

function LoadingSkeleton() {
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: "8px",
				marginTop: "30px",
			}}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "8px",
				}}
			>
				<Skeleton
					key={1}
					width={"70%"}
					height={20}
					style={{ transform: "none" }}
				></Skeleton>
			</div>
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					gap: "8px",
				}}
			>
				{[1, 2, 3].map((index) => {
					return (
						<Skeleton
							key={index}
							width={"150px"}
							height={180}
							style={{ transform: "none" }}
						></Skeleton>
					);
				})}
			</div>
		</div>
	);
}
